import React from "react";

/**
 * MenuItem
 *  - Text centered, optional icon(s) on left/right via `side`
 *
 * Props:
 *  - children: label/content (middle)
 *  - icon: string | ReactNode (for material-icons string or custom node)
 *  - side: 'left' | 'right' | 'both' | undefined (default: 'left')
 *  - iconLeft / iconRight: ReactNode (optional explicit overrides)
 *  - className: string
 *  - onClick: () => void
 *  - ...rest: forwarded button props
 */
export default function MenuItem({
  css,
  className,
  icon,
  iconLeft,
  iconRight,
  badge,
  badgeAriaLabel,
  label,
  showLabel = true,
  children,
  side = "left",
  active = false,
  as: Element = "div",
  ...rest
}) {
  const showLeft = side === "left" || side === "both";
  const showRight = side === "right" || side === "both";
  const resolvedLeft = iconLeft ?? (showLeft ? icon : null);
  const resolvedRight = iconRight ?? (showRight ? icon : null);
  const rootClass = [css, className].filter(Boolean).join(" ");
  const classes = ["menuitem", rootClass, active ? "menuitem-active" : null]
    .filter(Boolean)
    .join(" ");

  const elementProps = { className: classes, ...rest };

  if (Element === "button" && !("type" in elementProps)) {
    elementProps.type = "button";
  }

  return (
    <Element {...elementProps}>
      {resolvedLeft && (
        <span className="menuitem-icon-left">
          {typeof resolvedLeft === "string" ? (
            <span className="material-icons">{resolvedLeft}</span>
          ) : (
            resolvedLeft
          )}
        </span>
      )}
      {showLabel && (
        <span className="menuitem-label">{children || label}</span>
      )}
      {badge !== null && badge !== undefined && badge !== "" ? (
        <span
          className="menuitem-badge"
          aria-label={badgeAriaLabel || `Notifications ${badge}`}
        >
          {badge}
        </span>
      ) : null}

      {resolvedRight && (
        <span className="menuitem-icon-right">
          {typeof resolvedRight === "string" ? (
            <span className="material-icons">{resolvedRight}</span>
          ) : (
            resolvedRight
          )}
        </span>
      )}
    </Element>
  );
}
